import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Crown, Check, ArrowRight, Tag, Globe, IndianRupee, Sparkles, Clock, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth, apiCall } from "../App";
import { toast } from "sonner";

const Countdown = ({ endsAt }) => {
  const [timeLeft, setTimeLeft] = useState({});
  useEffect(() => {
    const calc = () => {
      const diff = new Date(endsAt) - Date.now();
      if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, expired: true };
      return { d: Math.floor(diff / 86400000), h: Math.floor((diff % 86400000) / 3600000), m: Math.floor((diff % 3600000) / 60000), s: Math.floor((diff % 60000) / 1000), expired: false };
    };
    setTimeLeft(calc());
    const id = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  if (timeLeft.expired) return null;
  return (
    <div className="flex items-center gap-1.5 mt-2 justify-center" data-testid="promo-countdown">
      <Clock className="w-3.5 h-3.5 text-divine-gold/70" />
      {[{v:timeLeft.d,l:"d"},{v:timeLeft.h,l:"h"},{v:timeLeft.m,l:"m"},{v:timeLeft.s,l:"s"}].map(({v,l}) => (
        <span key={l} className="bg-cosmic-void/60 px-2 py-1 rounded text-divine-gold font-mono text-sm min-w-[36px] text-center">{String(v).padStart(2,"0")}<span className="text-divine-gold/40 text-xs ml-0.5">{l}</span></span>
      ))}
    </div>
  );
};

const PricingPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currency, setCurrency] = useState(() => user?.client_region === "international" ? "USD" : "INR");
  const [billing, setBilling] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [couponValid, setCouponValid] = useState(null);
  const [validating, setValidating] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBilling, setShowBilling] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [promoBanner, setPromoBanner] = useState(null);
  const [referralCode, setReferralCode] = useState(() => searchParams.get("ref") || "");
  const [referralValid, setReferralValid] = useState(null);
  const [showManualCoupon, setShowManualCoupon] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    const fetchPlans = async () => {
      setLoading(true);
      const res = await apiCall("GET", `/subscription/plans?currency=${currency}`);
      if (res.success) setPlans(res.data.plans || []);
      setLoading(false);
    };
    fetchPlans();
  }, [currency]);

  useEffect(() => {
    apiCall("GET", "/system/active-promo").then(res => {
      if (res.success && res.data.banner) {
        setPromoBanner(res.data.banner);
        // Auto-apply promo coupon
        if (res.data.banner.coupon_code && !couponValid) {
          autoApplyCoupon(res.data.banner.coupon_code);
        }
      }
    });
    // Auto-validate referral from URL param
    const refParam = searchParams.get("ref");
    if (refParam) {
      apiCall("GET", `/referral/validate/${refParam}`).then(res => {
        if (res.success) { setReferralValid(res.data); toast.success(`Referral code applied! You'll get ${res.data.discount_percent}% off`); }
      });
    }
  }, []);

  const autoApplyCoupon = async (code) => {
    const res = await apiCall("POST", "/subscription/coupons/validate", { code, plan_id: "any" });
    if (res.success) {
      setCouponCode(code);
      setCouponValid(res.data);
    }
  };

  const filteredPlans = plans.filter(p => p.billing === billing);
  const relPlan = filteredPlans.find(p => p.module === "relationship");
  const lifePlan = filteredPlans.find(p => p.module === "life");
  const bundlePlan = filteredPlans.find(p => p.module === "bundle");

  const annualPlans = plans.filter(p => p.billing === "annual");
  const relAnnual = annualPlans.find(p => p.module === "relationship");
  const lifeAnnual = annualPlans.find(p => p.module === "life");
  const bundleAnnual = annualPlans.find(p => p.module === "bundle");

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidating(true);
    const res = await apiCall("POST", "/subscription/coupons/validate", {
      code: couponCode,
      currency: currency,
      billing: billing,
    });
    setValidating(false);
    if (res.success) {
      setCouponValid(res.data);
      toast.success(`Coupon "${res.data.code}" applied: ${res.data.discount_type === "percentage" ? `${res.data.discount_value}% off` : `${currency === "INR" ? "₹" : "$"}${res.data.discount_value} off`}`);
    } else {
      setCouponValid(null);
      toast.error(res.error || "Invalid coupon");
    }
  };

  const calcDiscount = (basePrice) => {
    if (!couponValid) return 0;
    if (couponValid.discount_type === "percentage") return basePrice * (couponValid.discount_value / 100);
    return couponValid.discount_value;
  };

  const handleSubscribe = async (plan) => {
    if (!token) { navigate("/login"); return; }
    if (!termsAccepted) { toast.error("Please accept the Terms of Service before proceeding"); return; }
    setProcessing(plan.plan_id);
    const res = await apiCall("POST", "/subscription/create-order", {
      plan_id: plan.plan_id,
      coupon_code: couponValid ? couponValid.code : "",
      referral_code: referralValid ? referralCode : "",
      terms_accepted: true,
      org_name: showBilling ? orgName : "",
      gst_number: showBilling ? gstNumber : "",
    }, token);
    if (res.success) {
      if (res.data.razorpay_order_id && res.data.razorpay_key_id) {
        // Real Razorpay checkout
        const options = {
          key: res.data.razorpay_key_id,
          amount: res.data.amount_smallest,
          currency: res.data.currency,
          name: "SIWON HARMONY",
          description: `${plan.name} - ${plan.billing}`,
          order_id: res.data.razorpay_order_id,
          handler: async (response) => {
            const verifyRes = await apiCall("POST", "/subscription/verify-payment", {
              order_id: res.data.order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            }, token);
            if (verifyRes.success) {
              toast.success("Subscription activated!");
              setTimeout(() => window.location.href = "/dashboard", 1500);
            } else {
              toast.error("Payment verification failed");
            }
          },
          prefill: { email: user?.email, name: user?.name },
          theme: { color: "#C8A22C" },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        // Test mode — auto-activate
        const activateRes = await apiCall("POST", "/subscription/test-activate", { plan_id: plan.plan_id }, token);
        if (activateRes.success) {
          toast.success(`${plan.name} activated! (Test mode)`);
          setTimeout(() => window.location.href = "/dashboard", 1500);
        } else {
          toast.error(activateRes.error || "Activation failed");
        }
      }
    } else {
      toast.error(res.error || "Order creation failed");
    }
    setProcessing(null);
  };

  const sym = currency === "INR" ? "₹" : "$";
  const moduleAccess = user?.module_access || [];

  const PlanCard = ({ plan, annualPlan, label, features, accent, isBundle }) => {
    if (!plan) return null;
    const base = plan.base_price;
    const disc = calcDiscount(base);
    const gstAmt = plan.gst_percent > 0 ? (base - disc) * (plan.gst_percent / 100) : 0;
    const total = Math.max(base - disc, 0) + gstAmt;
    const hasAccess = isBundle ? (moduleAccess.includes("relationship") && moduleAccess.includes("life")) : moduleAccess.includes(plan.module);

    return (
      <div className={`rounded-2xl border-2 ${isBundle ? "border-divine-gold/50 bg-divine-gold/5" : "border-divine-gold/15 bg-cosmic-light/5"} p-6 flex flex-col transition-all hover:border-divine-gold/40`}
        data-testid={`plan-card-${plan.plan_id}`}>
        {isBundle && (
          <div className="text-center mb-3">
            <span className="bg-divine-gold text-cosmic-void text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">Best Value</span>
          </div>
        )}
        <h3 className="font-heading text-lg text-divine-gold text-center">{label}</h3>
        <div className="text-center my-4">
          {disc > 0 && <p className="text-chakra-crown/40 text-sm line-through">{sym}{base.toLocaleString()}</p>}
          <p className="text-3xl font-bold text-divine-gold">
            {sym}{Math.round(base - disc).toLocaleString()}
            {plan.gst_percent > 0 && <span className="text-xs text-chakra-crown/40 font-normal"> + {plan.gst_percent}% GST</span>}
          </p>
          <p className="text-chakra-crown/50 text-xs mt-1">/{billing === "monthly" ? "month" : billing === "quarterly" ? "quarter" : "year"}</p>
          {(billing === "annual" || billing === "quarterly") && annualPlan && (
            <p className="text-emerald-400 text-xs mt-1">
              Effective {sym}{annualPlan.effective_monthly}/month {annualPlan.annual_saving > 0 ? `| Save ${sym}${annualPlan.annual_saving}` : ""}
            </p>
          )}
          {disc > 0 && <p className="text-emerald-400 text-xs mt-1">Total after GST: {sym}{Math.round(total).toLocaleString()}</p>}
        </div>
        <ul className="space-y-2 mb-6 flex-1">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-chakra-crown/70">
              <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        {hasAccess ? (
          <div className="text-center py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm font-heading" data-testid={`active-plan-${plan.plan_id}`}>
            Active
          </div>
        ) : (
          <Button
            onClick={() => handleSubscribe(plan)}
            disabled={processing === plan.plan_id}
            className={`w-full ${isBundle ? "bg-divine-gold text-cosmic-void hover:bg-divine-gold-light" : "vedic-button"}`}
            data-testid={`subscribe-${plan.plan_id}`}
          >
            {processing === plan.plan_id ? "Processing..." : "Subscribe Now"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-cosmic-void" data-testid="pricing-page">
      <nav className="fixed top-0 left-0 right-0 z-40 bg-cosmic-void/95 backdrop-blur-sm border-b border-divine-gold/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <span className="text-2xl text-divine-gold">ॐ</span>
            <span className="font-heading text-lg text-divine-gold tracking-wider hidden md:inline">SIWON HARMONY</span>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <>
                {(user.module_access || []).length > 0 || ["admin","coach","expert"].includes(user.role) ? (
                  <Button variant="outline" className="border-divine-gold/30 text-divine-gold" onClick={() => navigate(user.role === "admin" ? "/admin" : user.role === "coach" ? "/coach" : user.role === "expert" ? "/expert" : "/dashboard")} data-testid="go-dashboard-btn">
                    Go to Dashboard
                  </Button>
                ) : (
                  <Button variant="outline" className="border-divine-gold/30 text-divine-gold" onClick={() => navigate("/")} data-testid="go-home-btn">
                    Home
                  </Button>
                )}
                <Button variant="ghost" className="text-chakra-crown/50 hover:text-red-400 text-sm"
                  onClick={() => { localStorage.removeItem("siwon_token"); localStorage.removeItem("siwon_user"); window.location.href = "/"; }}
                  data-testid="pricing-logout-btn">
                  Logout
                </Button>
              </>
            ) : (
              <Button className="vedic-button" onClick={() => navigate("/login")} data-testid="login-btn">
                Login
              </Button>
            )}
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="font-heading text-4xl text-divine-gold tracking-wider mb-3">Simple, Transparent Pricing</h1>
            <p className="text-chakra-crown/60 text-base max-w-lg mx-auto">Choose your transformation path. Access the modules you need, upgrade anytime.</p>
          </div>

          {/* Promo Banner */}
          {promoBanner && new Date(promoBanner.ends_at) > new Date() && (
            <div className={`mb-6 p-4 rounded-2xl border text-center transition-all ${
              promoBanner.bg_color === "emerald" ? "bg-gradient-to-r from-emerald-500/15 to-teal-500/15 border-emerald-500/30" :
              promoBanner.bg_color === "purple" ? "bg-gradient-to-r from-purple-500/15 to-pink-500/15 border-purple-500/30" :
              promoBanner.bg_color === "red" ? "bg-gradient-to-r from-red-500/15 to-orange-500/15 border-red-500/30" :
              promoBanner.bg_color === "blue" ? "bg-gradient-to-r from-blue-500/15 to-cyan-500/15 border-blue-500/30" :
              "bg-gradient-to-r from-divine-gold/15 to-amber-500/15 border-divine-gold/30"
            }`} data-testid="promo-banner">
              <div className="flex items-center justify-center gap-3 flex-wrap">
                {promoBanner.discount_text && (
                  <span className={`font-heading text-2xl ${
                    promoBanner.bg_color === "emerald" ? "text-emerald-400" :
                    promoBanner.bg_color === "purple" ? "text-purple-400" :
                    promoBanner.bg_color === "red" ? "text-red-400" :
                    promoBanner.bg_color === "blue" ? "text-blue-400" :
                    "text-divine-gold"
                  }`}>{promoBanner.discount_text}</span>
                )}
                <span className="font-heading text-lg text-chakra-crown/90">{promoBanner.title}</span>
              </div>
              {promoBanner.subtitle && <p className="text-chakra-crown/60 text-xs mt-1">{promoBanner.subtitle}</p>}
              {promoBanner.coupon_code && (
                <div className="mt-3">
                  {couponValid && couponValid.code === promoBanner.coupon_code ? (
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-sm font-heading" data-testid="promo-applied-badge">
                      <Check className="w-4 h-4" /> {promoBanner.coupon_code} Applied — {couponValid.discount_type === "percentage" ? `${couponValid.discount_value}% off` : `${sym}${couponValid.discount_value} off`}
                    </span>
                  ) : (
                    <button onClick={() => autoApplyCoupon(promoBanner.coupon_code)}
                      className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-divine-gold text-cosmic-void font-heading text-sm hover:bg-divine-gold-light transition-all"
                      data-testid="apply-offer-btn">
                      <Tag className="w-4 h-4" /> Apply {promoBanner.discount_text || "Offer"}
                    </button>
                  )}
                </div>
              )}
              <Countdown endsAt={promoBanner.ends_at} />
            </div>
          )}

          {/* Currency Toggle */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <button onClick={() => setCurrency("INR")} className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all text-sm ${currency === "INR" ? "border-divine-gold bg-divine-gold/10 text-divine-gold" : "border-divine-gold/15 text-chakra-crown/50 hover:border-divine-gold/30"}`}
              data-testid="currency-inr">
              <IndianRupee className="w-4 h-4" /> Domestic (INR)
            </button>
            <button onClick={() => setCurrency("USD")} className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all text-sm ${currency === "USD" ? "border-divine-gold bg-divine-gold/10 text-divine-gold" : "border-divine-gold/15 text-chakra-crown/50 hover:border-divine-gold/30"}`}
              data-testid="currency-usd">
              <Globe className="w-4 h-4" /> International (USD)
            </button>
          </div>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <button onClick={() => setBilling("monthly")} className={`px-5 py-2 rounded-full text-sm font-heading transition-all ${billing === "monthly" ? "bg-divine-gold text-cosmic-void" : "text-chakra-crown/50 hover:text-divine-gold"}`}
              data-testid="billing-monthly">Monthly</button>
            <button onClick={() => setBilling("quarterly")} className={`px-5 py-2 rounded-full text-sm font-heading transition-all ${billing === "quarterly" ? "bg-divine-gold text-cosmic-void" : "text-chakra-crown/50 hover:text-divine-gold"}`}
              data-testid="billing-quarterly">Quarterly</button>
            <button onClick={() => setBilling("annual")} className={`px-5 py-2 rounded-full text-sm font-heading transition-all flex items-center gap-1 ${billing === "annual" ? "bg-divine-gold text-cosmic-void" : "text-chakra-crown/50 hover:text-divine-gold"}`}
              data-testid="billing-annual">
              Annual <Sparkles className="w-3 h-3" />
            </button>
          </div>

          {/* Plan Cards */}
          {loading ? (
            <p className="text-center text-chakra-crown/40">Loading plans...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <PlanCard plan={relPlan} annualPlan={relAnnual} label="Relationship Module"
                features={["12D Relationship Profiling", "Vikara & Guna Assessment", "Energy Polarity Analysis", "3A Framework Journey", "Harmony Practices", "Coach Support"]}
                accent="chakra-heart" />
              <PlanCard plan={lifePlan} annualPlan={lifeAnnual} label="Life Wisdom Coaching Module"
                features={["9-Module Life Diagnostic", "5 Kosha Framework", "Practice Universe (126+)", "Practice Streaks & Calendar", "Expert Guidance", "Life 3A Journey"]}
                accent="emerald" />
              <PlanCard plan={bundlePlan} annualPlan={bundleAnnual} label="Complete Bundle" isBundle
                features={["Everything in Relationship", "Everything in Life Wisdom Coaching", "Cross-Module Insights", "Priority Coach Access", "Full 3A Reports", "Maximum Savings"]}
                accent="divine-gold" />
            </div>
          )}

          {/* Applied Offers Summary + Manual Entry */}
          <div className="max-w-md mx-auto" data-testid="offers-section">
            {/* Show what's applied */}
            {(couponValid || referralValid) && (
              <div className="sacred-card mb-3 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    <span className="text-emerald-400 text-sm font-heading">Offers Applied</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {couponValid && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-400">
                      <Tag className="w-3 h-3" /> {couponValid.code}: {couponValid.discount_type === "percentage" ? `${couponValid.discount_value}% off` : `${sym}${couponValid.discount_value} off`}
                      <button onClick={() => { setCouponCode(""); setCouponValid(null); }} className="ml-1 text-emerald-400/50 hover:text-red-400" data-testid="remove-coupon">×</button>
                    </span>
                  )}
                  {referralValid && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sky-500/10 border border-sky-500/20 text-xs text-sky-400">
                      <Crown className="w-3 h-3" /> Referral: {referralValid.discount_percent}% reward after subscription
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Compact toggle for manual codes */}
            <div className="text-center">
              <button onClick={() => setShowManualCoupon(!showManualCoupon)}
                className="text-chakra-crown/40 text-xs hover:text-divine-gold transition-all"
                data-testid="toggle-manual-code">
                {showManualCoupon ? "Hide" : couponValid ? "Use a different code?" : "Have a coupon or referral code?"}
              </button>
            </div>

            {showManualCoupon && (
              <div className="sacred-card mt-3 space-y-3">
                {/* Coupon */}
                <div>
                  <label className="text-chakra-crown/50 text-xs mb-1 block">Coupon Code</label>
                  <div className="flex gap-2">
                    <Input value={couponCode} onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponValid(null); }}
                      placeholder="Enter coupon code"
                      className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown uppercase text-sm"
                      data-testid="coupon-input" />
                    <Button onClick={handleValidateCoupon} disabled={validating || !couponCode.trim()} size="sm"
                      variant="outline" className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10 shrink-0 text-xs"
                      data-testid="apply-coupon-btn">
                      {validating ? "..." : "Apply"}
                    </Button>
                  </div>
                </div>
                {/* Referral */}
                <div>
                  <label className="text-chakra-crown/50 text-xs mb-1 block">Referral Code</label>
                  <div className="flex gap-2">
                    <Input value={referralCode} onChange={(e) => { setReferralCode(e.target.value.toUpperCase()); setReferralValid(null); }}
                      placeholder="e.g. REF-VINOD-7X3K"
                      className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown font-mono uppercase text-sm"
                      data-testid="referral-code-input" />
                    <Button size="sm" variant="outline" className="border-divine-gold/20 text-divine-gold hover:bg-divine-gold/10 text-xs"
                      onClick={async () => {
                        if (!referralCode.trim()) return;
                        const res = await apiCall("GET", `/referral/validate/${referralCode}`);
                        if (res.success) { setReferralValid(res.data); toast.success(`Referral applied!`); }
                        else { setReferralValid(null); toast.error(res.error || "Invalid code"); }
                      }}
                      data-testid="validate-referral-btn">Apply</Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Terms of Service Consent */}
          <div className="max-w-lg mx-auto mt-6 p-4 rounded-xl border border-divine-gold/15 bg-cosmic-light/3" data-testid="terms-consent-section">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded border-divine-gold/30 accent-divine-gold shrink-0"
                data-testid="terms-checkbox" />
              <span className="text-xs text-chakra-crown/70 leading-relaxed">
                I have read, understood, and agree to the{" "}
                <Link to="/terms" target="_blank" className="text-divine-gold underline hover:text-divine-gold-light">Terms of Service</Link>,{" "}
                Service Disclaimer, Privacy Policy, and International Payment Notice.
                I understand that this is an online coaching/service subscription delivered from India.
              </span>
            </label>
            {!termsAccepted && (
              <p className="text-amber-400/60 text-xs mt-2 pl-7">You must accept the terms to subscribe.</p>
            )}
          </div>

          {/* GST / Billing Details (Optional) */}
          <div className="max-w-md mx-auto sacred-card mt-4" data-testid="billing-details-section">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={showBilling} onChange={(e) => setShowBilling(e.target.checked)}
                className="w-4 h-4 rounded border-divine-gold/30 accent-divine-gold"
                data-testid="billing-checkbox" />
              <span className="text-sm text-chakra-crown/70">Add billing details for GST invoice</span>
            </label>
            {showBilling && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="text-xs text-chakra-crown/50 block mb-1">Organization Name</label>
                  <Input value={orgName} onChange={(e) => setOrgName(e.target.value)}
                    placeholder="Your organization name"
                    className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown"
                    data-testid="org-name-input" />
                </div>
                <div>
                  <label className="text-xs text-chakra-crown/50 block mb-1">GST Registration Number</label>
                  <Input value={gstNumber} onChange={(e) => setGstNumber(e.target.value)}
                    placeholder="e.g. 22AAAAA0000A1Z5"
                    className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown"
                    data-testid="gst-number-input" />
                </div>
              </div>
            )}
          </div>

          {/* Footer note */}
          <p className="text-center text-chakra-crown/30 text-xs mt-8">
            All prices are exclusive of applicable taxes. Subscriptions auto-renew. Cancel anytime.
          </p>
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
